/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActionArea,
    Chip,
    Grid,
    Pagination,
    CircularProgress,
    Button,
    TextField,
    MenuItem,
    Stack,
    Divider,
} from "@mui/material";
import {
    BusinessOutlined,
    ReceiptLongOutlined,
    PeopleAltOutlined,
    LoginOutlined,
    SupportAgentOutlined,
} from "@mui/icons-material";
import { AppDispatch } from "../../../redux/store/store";
import {
    enterImpersonation,
    setActiveBusiness,
} from "../../../redux/slices/collectSlice";
import { fetchActiveBusiness } from "../../../redux/actions/collectAction";
import {
    submitGetAllBusinesses,
    submitGetAdminMe,
} from "../../../redux/services/collectService";
import { selectIsPlatformSuperAdmin } from "../../../redux/selectors/collectSelector";
import {
    IAdminBusinessCard,
    IAdminBusinessListPage,
} from "../../../utility/types/collectTypes";
import { colors } from "../../../utility/types/constant";
import { IMPERSONATION_STORAGE_KEY } from "./impersonationStorage";

const PAGE_SIZE = 6;

/** Format a minor-unit-agnostic amount into a localized currency string. */
function formatMoney(amount: number, currency?: string): string {
    const code = (currency || "usd").toUpperCase();
    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: code,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    } catch {
        return `${code} ${(amount || 0).toFixed(2)}`;
    }
}

function subscriptionLabel(card: IAdminBusinessCard): string {
    if (!card.subscription?.status) return "No subscription";
    const status = card.subscription.status.replace(/_/g, " ");
    const phase = card.subscription.planPhase
        ? ` · ${card.subscription.planPhase}`
        : "";
    return `${status}${phase}`;
}

function subscriptionColor(
    status?: string,
): "success" | "warning" | "error" | "default" {
    switch (status) {
        case "active":
        case "trialing":
        case "pending_cancellation":
            return "success";
        case "past_due":
        case "pending":
            return "warning";
        case "cancelled":
        case "trial_expired":
        case "past_due_expired":
        case "inactive":
            return "error";
        default:
            return "default";
    }
}

const CollectAdminDashboard: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const isPlatformSuperAdmin = useSelector(selectIsPlatformSuperAdmin);

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<
        "all" | "active" | "inactive"
    >("all");
    const [result, setResult] = useState<IAdminBusinessListPage | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Client-side defense-in-depth guard. `null` = still verifying, `true` =
    // authorized, `false` = redirecting away. Server remains authoritative.
    const [authorized, setAuthorized] = useState<boolean | null>(
        isPlatformSuperAdmin ? true : null,
    );

    const load = useCallback(
        async (targetPage: number, searchTerm: string, status: string) => {
            setIsLoading(true);
            setError(null);
            try {
                const res = await submitGetAllBusinesses({
                    page: targetPage,
                    limit: PAGE_SIZE,
                    search: searchTerm.trim() || undefined,
                    status: status as "all" | "active" | "inactive",
                });
                setResult(res);
            } catch (e: any) {
                setError(e?.message || "Failed to load businesses.");
                setResult(null);
            } finally {
                setIsLoading(false);
            }
        },
        [],
    );

    // Verify super-admin authorization before rendering the console. Uses the
    // cached Redux flag when available; otherwise probes the server directly so
    // a direct URL visit by a non-admin is cleanly redirected (not just 403'd).
    useEffect(() => {
        if (isPlatformSuperAdmin) {
            setAuthorized(true);
            return;
        }
        let mounted = true;
        submitGetAdminMe().then((isAdmin) => {
            if (!mounted) return;
            if (isAdmin) {
                setAuthorized(true);
            } else {
                setAuthorized(false);
                navigate("/collect/dashboard", { replace: true });
            }
        });
        return () => {
            mounted = false;
        };
    }, [isPlatformSuperAdmin, navigate]);

    useEffect(() => {
        if (authorized !== true) return;
        load(page, search, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authorized, page, statusFilter]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (page !== 1) setPage(1);
        else load(1, search, statusFilter);
    };

    const handleEnter = async (card: IAdminBusinessCard) => {
        // Persist so the impersonation survives a page refresh, then point the
        // active-tenant context at the target and hydrate its business + role.
        try {
            window.localStorage.setItem(
                IMPERSONATION_STORAGE_KEY,
                JSON.stringify({
                    businessId: card._id,
                    businessName: card.name,
                }),
            );
        } catch {
            /* non-fatal — impersonation still works for this session */
        }
        dispatch(setActiveBusiness(card._id));
        dispatch(
            enterImpersonation({
                businessId: card._id,
                businessName: card.name,
            }),
        );
        await dispatch(fetchActiveBusiness(card._id));
        navigate("/collect/dashboard");
    };

    const totalPages = result?.totalPages || 0;
    const businesses = result?.data || [];

    // Hold rendering until authorization is confirmed to avoid flashing the
    // console to a principal who is about to be redirected away.
    if (authorized !== true) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
            <Box
                sx={{
                    mb: 3,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Admin Console
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All businesses on the FundsyFi Recover platform. Open a
                        business to view and manage its account with full access.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<SupportAgentOutlined />}
                    onClick={() => navigate("/collect/admin/support")}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                >
                    Support Incident Queue
                </Button>
            </Box>

            {/* Filters */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mb: 3 }}
                component="form"
                onSubmit={handleSearchSubmit}
            >
                <TextField
                    size="small"
                    label="Search by name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1 }}
                />
                <TextField
                    size="small"
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(
                            e.target.value as "all" | "active" | "inactive",
                        );
                        setPage(1);
                    }}
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
                <Button type="submit" variant="outlined">
                    Search
                </Button>
            </Stack>

            {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && error && (
                <Box
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: "rgba(220, 38, 38, 0.06)",
                        color: "#b91c1c",
                        textAlign: "center",
                    }}
                >
                    <Typography sx={{ mb: 1 }}>{error}</Typography>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => load(page, search, statusFilter)}
                    >
                        Retry
                    </Button>
                </Box>
            )}

            {!isLoading && !error && businesses.length === 0 && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                    <BusinessOutlined
                        sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                    />
                    <Typography color="text.secondary">
                        No businesses found.
                    </Typography>
                </Box>
            )}

            {!isLoading && !error && businesses.length > 0 && (
                <>
                    <Grid container spacing={2.5}>
                        {businesses.map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card._id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: "100%",
                                        borderRadius: 3,
                                        transition:
                                            "box-shadow .18s, transform .18s",
                                        "&:hover": {
                                            boxShadow: 4,
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleEnter(card)}
                                        sx={{ height: "100%", p: 0.5 }}
                                    >
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "space-between",
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        pr: 1,
                                                    }}
                                                    title={card.name}
                                                >
                                                    {card.name}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        card.isActive
                                                            ? "Active"
                                                            : "Inactive"
                                                    }
                                                    color={
                                                        card.isActive
                                                            ? "success"
                                                            : "default"
                                                    }
                                                    variant="outlined"
                                                />
                                            </Box>

                                            <Chip
                                                size="small"
                                                label={subscriptionLabel(card)}
                                                color={subscriptionColor(
                                                    card.subscription?.status,
                                                )}
                                                sx={{
                                                    mb: 1.5,
                                                    textTransform: "capitalize",
                                                }}
                                            />

                                            {card.owner && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 1.5,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    title={
                                                        card.owner.email || ""
                                                    }
                                                >
                                                    {card.owner.name ||
                                                        card.owner.email ||
                                                        "—"}
                                                </Typography>
                                            )}

                                            <Divider sx={{ my: 1 }} />

                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                sx={{ mb: 0.5 }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <ReceiptLongOutlined
                                                        sx={{
                                                            fontSize: 16,
                                                            color: "text.secondary",
                                                        }}
                                                    />
                                                    <Typography variant="caption">
                                                        {
                                                            card.metrics
                                                                .openInvoiceCount
                                                        }{" "}
                                                        open
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {formatMoney(
                                                        card.metrics
                                                            .outstandingBalance,
                                                        card.currency,
                                                    )}{" "}
                                                    outstanding
                                                </Typography>
                                            </Stack>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                <PeopleAltOutlined
                                                    sx={{ fontSize: 16 }}
                                                />
                                                <Typography variant="caption">
                                                    {card.metrics.memberCount}{" "}
                                                    member
                                                    {card.metrics
                                                        .memberCount === 1
                                                        ? ""
                                                        : "s"}
                                                </Typography>
                                            </Box>

                                            <Button
                                                fullWidth
                                                size="small"
                                                variant="contained"
                                                startIcon={<LoginOutlined />}
                                                sx={{
                                                    mt: 2,
                                                    bgcolor: colors.primary,
                                                    "&:hover": {
                                                        bgcolor: colors.primary,
                                                        filter: "brightness(0.95)",
                                                    },
                                                }}
                                            >
                                                Open business
                                            </Button>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {totalPages > 1 && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 4,
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_e, value) => setPage(value)}
                                color="primary"
                                shape="rounded"
                            />
                        </Box>
                    )}

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", textAlign: "center", mt: 1 }}
                    >
                        {result?.total} business
                        {result?.total === 1 ? "" : "es"} total
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default CollectAdminDashboard;
